import { useState } from 'react';
import { apiRequest } from '@tpct/admin-ui';

type UseEditModeReturn<T> = {
  isEditing: boolean;
  localData: T;
  toggle: () => void;
  cancel: () => void;
  setField: (field: string, value: string | number | null | undefined) => void;
  save: (
    bootstrapKey: string,
    onSuccess: (updatedData?: T) => void,
    payloadExtras?: Partial<T>,
  ) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
};

const useEditMode = <T>(serverData: T, endpoint: string): UseEditModeReturn<T> => {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState<T>(serverData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const toggle = () => {
    setLocalData(serverData);
    setIsEditing(true);
    setSaveError(null);
  };

  const cancel = () => {
    setLocalData(serverData);
    setIsEditing(false);
    setSaveError(null);
  };

  const setField = (field: string, value: string | number | null | undefined) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));
  };

  const save = async (
    bootstrapKey: string,
    onSuccess: (updatedData?: T) => void,
    payloadExtras?: Partial<T>,
  ) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedData = await apiRequest<T>(
        endpoint,
        { method: 'PATCH', body: JSON.stringify({ ...localData, ...payloadExtras }) },
        bootstrapKey
      );
      setIsEditing(false);
      onSuccess(updatedData);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  };

  return { isEditing, localData, toggle, cancel, setField, save, isSaving, saveError } as const;
};

export default useEditMode;
